import { errorId, helpTextId } from 'volta/utils/id';

import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { dasherize } from '@ember/string';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { getColorCssVar, THueVariationString } from 'volta/utils/style-utils';

function invertNumber(n: number) {
  if (Math.sign(n) === 1) {
    return -Math.abs(n);
  } else if (Math.sign(n) === -1) {
    return Math.abs(n);
  } else {
    return 0;
  }
}

/**
 * Bevolta range slider component.
 */

interface IArgs {
  // Label for the range input
  label: string;

  // Adds an action to the label
  labelAction: { [key: string]: unknown };

  // Visually hide the label
  labelHidden: boolean;

  // Increment value for range input changes
  step: number;

  // Provide a tooltip while sliding, indicating the current value
  output: boolean;

  // Unit to display after the value
  outputUnit?: string;

  // Should the value be outputted as percent ?
  percent: boolean;

  // Element to display before the input
  prefix: string | Component;

  // Element to display after the input
  suffix: string | Component;

  // Callback when the range input is changed
  onChange: (value: number, id: string) => void;

  // Callback when the range input is changed
  onSlide: (value: number, id: string) => void;

  // Callback when range input is focused
  onFocus: (value: number, id: string) => void;

  // Callback when focus is removed
  onBlur: (value: number, id: string) => void;

  // Initial value for range input
  value?: number;

  // Minimum possible value for range input
  min?: number;

  // Maximum possible value for range input
  max?: number;

  error?: string;

  helpText?: string;

  trackColor?: THueVariationString;

  trackBg?: THueVariationString;

  isRequired?: boolean;
}

export default class BvSlider extends Component<IArgs> {
  /**
   * ID for range input
   *
   * Defaults to Ember's internal GUID for the component instance
   *
   * @property {id}
   */
  id: string = guidFor(this);

  get value() {
    return this.args.value ?? 0;
  }

  get min() {
    return this.args.min ?? 0;
  }

  get max() {
    return this.args.max ?? 100;
  }

  dataTestRangeSlider = true;

  /**
   * Style for the range wrapper div
   *
   * @property {rangeWrapperStyle}
   * @type {String}
   * @private
   */
  get rangeWrapperStyle() {
    const { sliderProgress, min, max } = this;
    const { value: current } = this.args;

    const styleProps = Object.assign(
      { min, max, current },
      {
        progress: `${sliderProgress}%`,
        outputFactor: invertNumber((sliderProgress - 50) / 100)
      }
    );

    const styleString = Object.keys(styleProps).reduce((styleStr, propName) => {
      return `${styleStr} --bv-slider-${dasherize(propName)}:${
        styleProps[propName as keyof typeof styleProps]
      };`;
    }, '');
    const trackBg = this.args.trackBg
      ? `--bv-slider-track-bg:${getColorCssVar(this.args.trackBg)};`
      : '';
    const trackColor = this.args.trackColor
      ? `--bv-slider-track-color:${getColorCssVar(this.args.trackColor)};`
      : '';
    return htmlSafe(trackColor + trackBg + styleString.trim());
  }

  /**
   * Slider progress percentage
   *
   * @property {sliderProgress}
   * @type {Number}
   * @private
   */
  get sliderProgress() {
    const { min, max, value } = this;
    return ((value - min) * 100) / (max - min);
  }

  /**
   * Accessibility
   *
   * @property {ariaDescribedBy}
   * @type {Boolean}
   * @private
   */
  get ariaDescribedBy() {
    const { error, helpText } = this.args;
    const { id } = this;
    const describedBy = [];

    if (error) {
      describedBy.push(errorId(id));
    }

    if (helpText) {
      describedBy.push(helpTextId(id));
    }

    return describedBy.length ? describedBy.join(' ') : undefined;
  }

  @action
  handleChange(event?: Event) {
    if (!event) {
      return;
    }
    const currentTarget = event.currentTarget as EventTarget & { value?: string };
    if (currentTarget?.value) {
      this.args.onChange?.(parseFloat(currentTarget.value), this.id);
    }
  }

  @action
  handleSlide(event?: Event) {
    if (!event) {
      return;
    }
    const currentTarget = event.currentTarget as EventTarget & { value?: string };
    if (currentTarget?.value) {
      this.args.onSlide?.(parseFloat(currentTarget.value), this.id);
    }
  }
}
